// components
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const SPEED = 30;

const ReferencesCarousel = ({ references, carouselVariant: variant }) => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!references || references.length === 0) return;

    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const initializeCarousel = () => {
      // Vytvoříme dostatek duplikátů pro plynulý scroll
      const viewportWidth = scrollElement.parentElement.clientWidth;

      // Spočítáme průměrnou šířku loga (odhadneme ~150px)
      const estimatedItemWidth = 150;
      const itemsNeeded = Math.ceil((viewportWidth * 3) / estimatedItemWidth);

      const duplicatedItems = [];
      for (let i = 0; i < itemsNeeded; i++) {
        duplicatedItems.push(references[i % references.length]);
      }

      setItems(duplicatedItems);

      let position = 0;

      const animate = () => {
        position -= SPEED / 60; // 60 FPS

        // Spočítáme celkovou šířku originálních položek
        const itemWidths = Array.from(scrollElement.children).slice(0, references.length);
        const totalOriginalWidth = itemWidths.reduce((sum, child) => {
          return sum + child.offsetWidth + 48; // 48px = gap-12 (3rem)
        }, 0);

        // Když se posuneme o šířku originálních položek, resetujeme
        if (Math.abs(position) >= totalOriginalWidth) {
          position = position + totalOriginalWidth;
        }

        scrollElement.style.transform = `translateX(${position}px)`;

        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    };

    // Počkáme na načtení obrázků
    const timer = setTimeout(initializeCarousel, 100);

    // Resetujeme animaci při změně velikosti okna
    const handleResize = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (scrollElement) {
        scrollElement.style.transform = 'translateX(0)';
      }
      clearTimeout(timer);
      setTimeout(initializeCarousel, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [references]);

  return (
    <VisibleLoading delay={300}>
      {({ ref, className }) => (
        <div ref={ref} className={`relative w-full overflow-hidden ${className || ''}`.trim()}>
          <div ref={scrollRef} className="flex items-center gap-12 px-6">
            {items.map((reference, index) => (
              <div key={`item-${index}`} className="flex shrink-0 items-center" style={{ minWidth: '80px' }}>
                {variant === 'color' && reference.imageColor ? (
                  <Image
                    src={getImagePath(reference.imageColor)}
                    alt={reference.name}
                    width={reference.imageColor.width}
                    height={reference.imageColor.height}
                    className="max-h-9 w-auto min-w-[80px] object-contain"
                  />
                ) : (
                  <Image
                    src={getImagePath(reference.image)}
                    alt={reference.name}
                    width={reference.image.width}
                    height={reference.image.height}
                    className="max-h-9 w-auto min-w-[80px] object-contain opacity-50 invert"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </VisibleLoading>
  );
};
export default ReferencesCarousel;
